import { useCallback, useEffect, useRef, useState } from 'react';
import { conciergeStarters } from '@/mocks/concierge';
import { useFavorites } from '@/hooks/useFavorites';
import GuestGuideAnswer from '@/components/feature/GuestGuideAnswer';
import { answerConcierge } from './conciergeEngine';

type Message = {
  id: string;
  role: 'guest' | 'concierge';
  text: string;
  refs?: string[];
  action?: 'contact';
};

const seedMessages: Message[] = [
  {
    id: 'intro',
    role: 'concierge',
    text: 'Hello \u2014 welcome to Inspire. I can tell you about our properties and which might suit your trip. Ask me anything, or tap a suggestion below.',
  },
];

export default function Concierge() {
  const [messages, setMessages] = useState<Message[]>(seedMessages);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { favorites } = useFavorites();

  const respondToGuest = useCallback((q: string) => {
    setMessages((prev) => [...prev, { id: `g-${Date.now()}`, role: 'guest', text: q }]);
    setThinking(true);

    setTimeout(() => {
      const match = answerConcierge(q, undefined, favorites);
      setMessages((prev) => [
        ...prev,
        { id: `c-${Date.now()}`, role: 'concierge', text: match.answer, refs: match.refs, action: match.action },
      ]);
      setThinking(false);
    }, 650);
  }, [favorites]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, thinking]);

  useEffect(() => {
    const onTripCriteria = (event: Event) => {
      const customEvent = event as CustomEvent<{ message?: string }>;
      const q = customEvent.detail?.message?.trim();
      if (q) respondToGuest(q);
    };

    window.addEventListener('inspire:trip-criteria', onTripCriteria);
    return () => window.removeEventListener('inspire:trip-criteria', onTripCriteria);
  }, [respondToGuest]);

  const send = (raw: string) => {
    const q = raw.trim();
    if (!q) return;

    setInput('');
    respondToGuest(q);
  };

  return (
    <section id="concierge" className="relative bg-foreground-950 text-background-50 py-20 md:py-32">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.24em] text-accent-300 mb-4">
              <span className="inline-block w-6 h-px bg-accent-500 align-middle mr-3" />
              Meet The Inspire Insider
            </div>
            <h2 className="font-heading text-4xl md:text-6xl leading-[1.05] tracking-[-0.01em]">
              A helpful hand,
              <span className="block italic font-light text-secondary-200">whenever you want it.</span>
            </h2>
            <p className="mt-6 text-background-100/80 text-base md:text-lg font-light leading-relaxed max-w-md">
              <strong className="font-semibold">Ask Anything:</strong> Get instant answers about any property in our collection.
              <br /><br />
              <strong className="font-semibold">Save Your Favorites:</strong> Click the ❤️ on properties you love to easily compare them side-by-side.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl bg-background-50 text-foreground-900 overflow-hidden border border-background-200/70 flex flex-col h-[560px] md:h-[600px]">
              <div className="flex items-center justify-between px-5 md:px-6 py-4 border-b border-background-200/70 bg-background-100/60">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-950 text-background-50">
                    <i className="ri-service-line text-sm" />
                  </span>
                  <div>
                    <p className="font-heading text-lg leading-tight">The Inspire Insider</p>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-500">
                      Your guide to the right stay
                    </p>
                  </div>
                </div>
                <span className="hidden sm:inline text-[11px] uppercase tracking-[0.22em] text-accent-700">
                  <i className="ri-heart-3-line mr-1.5" />
                  At your service
                </span>
              </div>

              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-5 md:px-6 py-6 space-y-4 subtle-scroll"
              >
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`flex ${m.role === 'guest' ? 'justify-end' : 'justify-start'} animate-msg-in`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm md:text-[15px] leading-relaxed ${
                        m.role === 'guest'
                          ? 'bg-foreground-950 text-background-50 rounded-br-md'
                          : 'bg-background-100 text-foreground-900 rounded-bl-md'
                      }`}
                    >
                      {m.role === 'concierge' ? <GuestGuideAnswer text={m.text} refs={m.refs} /> : <p>{m.text}</p>}
                    </div>
                  </div>
                ))}
                {thinking && (
                  <div className="flex justify-start animate-msg-in">
                    <div className="bg-background-100 text-foreground-500 rounded-2xl rounded-bl-md px-4 py-3 text-sm flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-pulse" />
                      <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-pulse [animation-delay:120ms]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-pulse [animation-delay:240ms]" />
                    </div>
                  </div>
                )}
              </div>

              <div className="px-5 md:px-6 py-3 border-t border-background-200/70 bg-background-100/40">
                <div className="flex gap-2 overflow-x-auto subtle-scroll pb-1">
                  {conciergeStarters.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => send(s)}
                      className="text-xs whitespace-nowrap px-3 py-2 rounded-lg border border-[#DED0BE] bg-[#EFE4D6] text-foreground-800 hover:border-[#B99F87] hover:text-foreground-950 cursor-pointer transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  send(input);
                }}
                className="px-4 md:px-5 py-4 border-t border-background-200/70 bg-background-50"
              >
                <div className="flex items-center gap-3 rounded-xl border border-[#CDBCA8] bg-background-100 px-4 py-2.5 shadow-sm focus-within:border-accent-600 focus-within:bg-background-50 transition-colors">
                  <i className="ri-question-answer-line text-lg text-accent-700" aria-hidden="true" />
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your question here..."
                    className="min-w-0 flex-1 bg-transparent text-sm md:text-base text-foreground-900 placeholder:text-foreground-500 focus:outline-none py-2"
                    aria-label="Ask The Inspire Insider"
                  />
                  <button
                    type="submit"
                    disabled={!input.trim()}
                    className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-foreground-950 text-background-50 hover:bg-accent-600 disabled:bg-foreground-300 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer transition-colors"
                    aria-label="Send"
                  >
                    <i className="ri-arrow-up-line" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
